/**
 * What the palette reports about itself.
 *
 * These are the **first custom PostHog events inside `/dashboard`**: everything
 * else in the repo captures on marketing surfaces only, so the shape here is the
 * one the next feature will copy. Two decisions worth keeping:
 *
 *  1. `snake_case`, prefixed by the surface, matching `landing_page_cta_activated`.
 *  2. Every event carries `org_id`. Person properties hold only email, name and
 *     geoip, so without it there is no way to tell an enterprise account from a
 *     self-serve planner after the fact, and "do enterprise users search
 *     differently" becomes unanswerable.
 *
 * Note that `PHProvider` only mounts PostHog when `isProduction` (which covers
 * Vercel preview as well as production), so `usePostHog()` is undefined in local
 * dev. Every call site must tolerate that rather than guard on it.
 */
export const PALETTE_EVENTS = {
  opened: "command_palette_opened",
  selected: "command_palette_item_selected",
  /** The one that earns this ticket: a query that found nothing. */
  searchEmpty: "command_palette_search_empty",
  chatHandoff: "command_palette_chat_handoff",
} as const;

/**
 * How long a query must sit unchanged before its empty result counts as a miss.
 *
 * Without this every prefix of every word is a "miss": typing "marriott" would
 * log m, ma, mar, marr and so on, and the log would describe typing speed rather
 * than intent. The delay is what makes a row in this log mean "someone looked
 * for this and gave up".
 */
export const EMPTY_QUERY_SETTLE_MS = 800;

/** Shortest query worth logging. One or two characters is noise, not a search. */
export const MIN_LOGGED_QUERY_LENGTH = 3;

/** Stand-in for an address, so the log keeps the shape of the search without the person. */
export const REDACTED_EMAIL = "[email]";

/** Deliberately loose: anything address-shaped should not reach analytics as itself. */
const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/g;

/**
 * Normalizes a query for the zero-result log, or rejects it.
 *
 * Case and whitespace are flattened so "Marriott " and "marriott" are one row
 * rather than two, which matters because this log is meant to be counted.
 *
 * Anything address-shaped is replaced rather than sent. This log exists to
 * capture what people type, and a search box invites a venue contact's address
 * as readily as a hotel name: "who did I email at the Marriott" is a perfectly
 * natural thing to paste in. Redacting keeps the row countable ("someone
 * searched for an address here") without putting the address in analytics.
 *
 * @param raw - The raw query as typed.
 * @returns The normalized query, or null when it is not worth logging.
 */
export function loggableQuery(raw: string): string | null {
  const query = raw.trim().toLowerCase().replace(/\s+/g, " ").replace(EMAIL_PATTERN, REDACTED_EMAIL);
  if (query.length < MIN_LOGGED_QUERY_LENGTH) return null;
  return query;
}
